import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function RouteLaneConfig({ roles, onCreateTab, toastMessages, currentMenu }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // Keep this array identity stable to avoid unnecessary re-renders/refetches
    const removeContextItems = useMemo(() => ['Print', 'Copy', 'Delete', 'Create'], []);

    // Centralize route path used by CRUD tabs
    const CRUD_ROUTE = '/home/routes/routeLaneConfigCrud';

    //===========================================================================================================
    // openRouteLaneCrud:
    // - Stable (useCallback) action dispatcher consumed by MTable context actions.
    // - Persists 'menu' and 'operation' in global state. For View/Edit, persists 'RouteLaneLine' as well.
    // - Validates selection and shows a localized toast when no row is selected.
    // - Builds a descriptive tab label using Route and Lane (null-safe).
    // - Using a stable callback prevents prop identity changes that could trigger extra renders/refetches.
    //===========================================================================================================
    const openRouteLaneCrud = useCallback(
        (operation, row) => {
            // Persist current menu and operation in global state
            dispatch(M_StateActions.addM_State({ key: 'menu', value: currentMenu }));
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));
            // For View/Edit, ensure a row is selected and persist it; otherwise, show an error toast
            if (!row) {
                toastMessages('error', '', t('SelectedLine'));
                return;
            }

            // Persist the selected RouteLaneLine in global state
            dispatch(M_StateActions.addM_State({ key: 'RouteLaneLine', value: row }));

            // Build a descriptive tab label using Route and Lane, handling null/undefined values
            const tabLabel =
                operation === 'View'
                    ? `${t('LabelViewRouteLane')} (${row?.Route ?? ''} - ${row?.Lane ?? ''})`
                    : `${t('LabelEditRouteLane')} (${row?.Route ?? ''} - ${row?.Lane ?? ''})`;

            onCreateTab(CRUD_ROUTE, tabLabel); // Open the CRUD tab with the constructed label
        },
        [dispatch, onCreateTab, t, toastMessages, currentMenu]
    );

    //===========================================================================================================
    // replaceFunctions:
    // - Memoized Map passed to MTable to resolve context actions.
    // - Kept stable with useMemo to avoid unnecessary re-renders and duplicate API calls.
    //===========================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openRouteLaneCrud('View', row)],
                ['Edit', (row) => openRouteLaneCrud('Edit', row)],
                // 'Create' and 'Delete' are intentionally omitted and also removed via removeContextItems
            ]),
        [openRouteLaneCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div style={{ color: ctx.secondaryColor }}>{t("RouteLaneRelations")}</div>
                    <div className='labelSubtitleSettings'>{t("InfoRouteLaneRelations")}</div>
                </div>
            </div>

            <MTable // dynamic table for Route Lane Relations (the M:N relationship between Routes and Lanes)
                filtersOnTable
                entityName={'RouteLaneRels'}
                roleName={'Locations'} // Permissions tied to 'Locations' role
                rolePermissions={roles?.map(vl => vl.Value)} // Array of permission strings
                removeContextItems={removeContextItems} 
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // Custom action handlers
                tabsRailWidth={250}
            />
        </div>
    );
}

RouteLaneConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
